import { Columns3, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CeQueLeClientAEnvoye } from './CeQueLeClientAEnvoye';
import {
  TiroirColonnesSaisie,
  type ColonneReglee,
} from './TiroirColonnesSaisie';
import { api } from '../services/api';
import { colonnesSaisie } from '../services/colonnes.service';

interface ReglageDossier {
  jourButoir: number | null;
  relanceAutomatique: boolean | null;
  delaiHeures: number | null;
}

const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/**
 * L'onglet « Variables » d'un dossier : ce que le client a envoyé pour le
 * mois, le lien à lui envoyer maintenant, et les colonnes de sa grille.
 * Un écran = une tâche (règle 14) : rien d'autre.
 */
export function OngletVariables({
  folderUuid,
  nomDossier,
  peutEcrireDirectement,
  ouvrirLesColonnes = false,
}: {
  folderUuid: string;
  nomDossier: string;
  peutEcrireDirectement: boolean;
  /** Arrivée depuis Paramètres → masque de saisie : le tiroir s'ouvre seul. */
  ouvrirLesColonnes?: boolean;
}) {
  const aujourdhui = new Date();
  const [mois, setMois] = useState(aujourdhui.getMonth() + 1);
  const [annee, setAnnee] = useState(aujourdhui.getFullYear());
  const [envoi, setEnvoi] = useState(false);
  // Un lecteur ne règle rien : l'URL `?colonnes=1` ne lui ouvre pas le tiroir.
  const [tiroir, setTiroir] = useState(
    ouvrirLesColonnes && peutEcrireDirectement
  );
  const [colonnes, setColonnes] = useState<ColonneReglee[]>([]);
  const [colonnesEnCours, setColonnesEnCours] = useState(false);
  /*
    Ce qui est convenu avec CE client : le jour du mois avant lequel il rend
    ses éléments (c'est la date que sa page annonce), et s'il est relancé tout
    seul quand il est en retard. Un champ vidé = rien de particulier pour lui.
  */
  const [reglage, setReglage] = useState<ReglageDossier | null>(null);
  const [jourSaisi, setJourSaisi] = useState('');
  const [reglageEnCours, setReglageEnCours] = useState(false);

  useEffect(() => {
    void colonnesSaisie
      .lister(folderUuid)
      .then(setColonnes)
      .catch(() => setColonnes([]));
    void api
      .get('/saisie-client/reglage-dossier', { params: { folderUuid } })
      .then(r => {
        const v = (r.data?.data ?? r.data ?? null) as ReglageDossier | null;
        setReglage(v);
        setJourSaisi(v?.jourButoir ? String(v.jourButoir) : '');
      })
      .catch(() => setReglage(null));
  }, [folderUuid]);

  const enregistrerReglage = async (
    jour: number | null,
    relance: boolean | null
  ) => {
    setReglageEnCours(true);
    try {
      const r = await api.post('/saisie-client/reglage-dossier', {
        folderUuid,
        jourButoir: jour,
        relanceAutomatique: relance,
        delaiHeures: reglage?.delaiHeures ?? null,
      });
      const v = (r.data?.data ?? r.data ?? null) as ReglageDossier | null;
      setReglage(v);
      setJourSaisi(v?.jourButoir ? String(v.jourButoir) : '');
    } catch {
      toast.error('Le réglage n’a pas été enregistré.');
      setJourSaisi(reglage?.jourButoir ? String(reglage.jourButoir) : '');
    } finally {
      setReglageEnCours(false);
    }
  };

  const precedent = () => {
    if (mois === 1) {
      setMois(12);
      setAnnee(a => a - 1);
    } else setMois(m => m - 1);
  };
  const suivant = () => {
    if (mois === 12) {
      setMois(1);
      setAnnee(a => a + 1);
    } else setMois(m => m + 1);
  };

  /*
    LE MASQUE DE SAISIE SE PRÉVOIT AVANT D'ENVOYER (décision Rose du
    22/09/2026). Sans colonne réglée, le client n'a rien à remplir dans
    « Variables » : le tiroir s'ouvre d'abord, et le lien part en le fermant.
  */
  const [envoiApresReglage, setEnvoiApresReglage] = useState(false);

  const demanderEnvoi = () => {
    if (colonnes.filter(c => c.active).length === 0) {
      setEnvoiApresReglage(true);
      setTiroir(true);
      return;
    }
    void envoyerLeLien();
  };

  /** Le geste explicite du tiroir : régler, puis envoyer. Fermer renonce. */
  const reglerPuisEnvoyer = () => {
    setTiroir(false);
    setEnvoiApresReglage(false);
    void envoyerLeLien();
  };

  const envoyerLeLien = async () => {
    setEnvoi(true);
    try {
      // Le secret n'est jamais restituable après coup : création et envoi se
      // font dans la foulée, côté serveur, avec l'adresse de cette plateforme.
      const r = await api.post('/saisie-client/liens/envoyer-en-groupe', {
        folderUuids: [folderUuid],
        mois,
        annee,
        origine: window.location.origin,
      });
      const bilan = (r.data?.data ?? r.data) as {
        envoyes?: number;
        detail?: { motif?: string }[];
      };
      if (bilan?.envoyes && bilan.envoyes > 0) {
        toast.success(`Lien de ${MOIS[mois - 1]} envoyé au client.`);
      } else {
        toast.error(
          bilan?.detail?.[0]?.motif ?? 'Le lien n’a pas pu être envoyé.'
        );
      }
    } catch {
      toast.error('Le lien n’a pas pu être envoyé.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={precedent}
          className="rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50"
          aria-label="Mois précédent"
        >
          ‹
        </button>
        <span className="min-w-[10rem] text-center text-sm font-semibold text-neutral-900">
          {MOIS[mois - 1]} {annee}
        </span>
        <button
          type="button"
          onClick={suivant}
          className="rounded-lg border border-neutral-200 px-2.5 py-1.5 text-sm text-neutral-600 hover:bg-neutral-50"
          aria-label="Mois suivant"
        >
          ›
        </button>
        <div className="ml-auto flex gap-2">
          {peutEcrireDirectement && (
            <button
              type="button"
              onClick={() => setTiroir(true)}
              className="flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              <Columns3 className="h-4 w-4" />
              Colonnes de saisie
            </button>
          )}
          <button
            type="button"
            onClick={demanderEnvoi}
            disabled={envoi || !peutEcrireDirectement}
            className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {envoi ? 'Envoi…' : 'Envoyer le lien au client'}
          </button>
        </div>
      </div>

      {/* Une seule ligne, deux réglages, posés là où la question se pose. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-neutral-700">
        <label className="flex items-center gap-2">
          <span>Date convenue avec le client : le</span>
          <input
            type="number"
            min={1}
            max={31}
            value={jourSaisi}
            disabled={reglageEnCours || !peutEcrireDirectement}
            onChange={e => setJourSaisi(e.target.value)}
            onBlur={() => {
              const n = Number(jourSaisi);
              const jour = jourSaisi && n >= 1 && n <= 31 ? n : null;
              if (jourSaisi && jour === null) {
                setJourSaisi(
                  reglage?.jourButoir ? String(reglage.jourButoir) : ''
                );
                return;
              }
              if (jour === (reglage?.jourButoir ?? null)) return;
              void enregistrerReglage(
                jour,
                reglage?.relanceAutomatique ?? null
              );
            }}
            aria-label="Jour du mois avant lequel le client rend ses éléments"
            className="focus:border-primary-500 focus:ring-primary-100 w-14 rounded-lg border border-neutral-300 px-2 py-1 text-center focus:ring-2 disabled:opacity-50"
            placeholder="—"
          />
          <span>du mois</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={reglage?.relanceAutomatique === true}
            disabled={reglageEnCours || !peutEcrireDirectement}
            onChange={e =>
              void enregistrerReglage(
                reglage?.jourButoir ?? null,
                e.target.checked ? true : null
              )
            }
            className="text-primary-600 h-4 w-4 rounded border-neutral-300"
          />
          <span>Relancer ce client automatiquement s’il est en retard</span>
        </label>
      </div>

      <CeQueLeClientAEnvoye
        folderUuid={folderUuid}
        mois={mois}
        annee={annee}
        peutEcrireDirectement={peutEcrireDirectement}
      />

      <TiroirColonnesSaisie
        ouvert={tiroir}
        nomDossier={nomDossier}
        colonnes={colonnes}
        enregistrement={colonnesEnCours}
        onFermer={() => {
          setTiroir(false);
          setEnvoiApresReglage(false);
        }}
        actionFinale={
          envoiApresReglage
            ? {
                libelle: 'Envoyer le lien au client',
                onClick: reglerPuisEnvoyer,
              }
            : undefined
        }
        onEnregistrer={async c => {
          setColonnesEnCours(true);
          try {
            await colonnesSaisie.enregistrer({
              folderUuid,
              uuid: c.uuid,
              code: c.code!,
              libelle: c.libelle!,
              unite: c.unite,
              valeurUnitaire: c.valeurUnitaire,
              aide: c.aide,
            });
            setColonnes(await colonnesSaisie.lister(folderUuid));
          } finally {
            setColonnesEnCours(false);
          }
        }}
        onChangerEtat={async (uuid, active) => {
          await colonnesSaisie.changerEtat(uuid, active);
          setColonnes(await colonnesSaisie.lister(folderUuid));
        }}
        onReordonner={async uuids => {
          setColonnes(l => {
            const par = new Map(l.map(c => [c.uuid, c]));
            const ranges = uuids.map(u => par.get(u)!).filter(Boolean);
            return [...ranges, ...l.filter(c => !uuids.includes(c.uuid))];
          });
          setColonnes(await colonnesSaisie.reordonner(folderUuid, uuids));
        }}
      />
    </div>
  );
}
